<script setup lang="ts">
/**
 * The message under a field: what is wrong and how to fix it, in plain words.
 *
 * A field points at it with `aria-describedby="<id>"` and sets `aria-invalid="true"` while the message
 * shows, so the error reaches a screen reader as well as the eye. It is announced politely, so typing
 * is not interrupted. Without a message it renders nothing, so a field's `aria-describedby` can keep
 * pointing at the id without describing anything.
 *
 * The polite live region is always in the page, and only the message comes and goes inside it: a screen
 * reader announces a change to a region it already knows, not a region that arrives with its text. With
 * no message the region is empty and out of the flow, so it adds no gap to the grid or stack it sits in.
 */
defineProps<{
    /** What is wrong. Nothing renders without it. */
    message?: string
    /** How to fix it. */
    fix?: string
    /** The id a field's `aria-describedby` points at. */
    id: string
}>()
</script>

<template>
    <div class="zh-field-error-region" :class="{ 'zh-field-error-region--idle': !message }" aria-live="polite" aria-atomic="true">
        <p v-if="message" :id="id" class="zh-field-error">
            <span class="zh-field-error__message">{{ message }}</span>
            <span v-if="fix" class="zh-field-error__fix"> {{ fix }}</span>
        </p>
    </div>
</template>

<style scoped>
/* Empty, the region stays in the accessibility tree but takes no track, gap or margin in its parent's layout. */
.zh-field-error-region--idle {
    position: absolute;
    width: 0;
    height: 0;
    margin: 0;
    overflow: hidden;
}

.zh-field-error {
    margin: 0.25rem 0 0;
    font-family: var(--font-mono);
    font-size: 0.75rem;
    line-height: 1.4;
    letter-spacing: 0.02em;
    color: var(--warning-amber);
}

.zh-field-error__fix {
    color: var(--dirty-white);
}
</style>
